import React, { useState, useCallback, useEffect } from 'react'
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  Card,
  Typography,
  message,
  Tooltip,
  Switch,
  Divider,
  notification
} from 'antd'
import {
  faRulerVertical,
  faWeightScale,
  faRuler,
  faChartLine,
  faCalculator,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayjs from 'dayjs'
import { createClinicDataRecords } from '../../../examination-utils/examination-lib'
import { GENERAL_STAT_REFERENCE_RANGES } from '../utils/general-stats-utils'
import { hexToRGBA, handleError, calculateBMI } from '../../../../lib/utils'

const { Title, Text } = Typography

const GeneralStatsCaptureModal = ({
  visible,
  onClose,
  bookingData,
  userRef,
  websocketConnection,
  onSuccess,
  token,
  agiliteUser
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [enabledStats, setEnabledStats] = useState({
    height: true,
    weight: true,
    waist: true
  })
  const [calculatedBMI, setCalculatedBMI] = useState(null)

  const statConfigs = [
    {
      key: 'height',
      title: 'Height',
      icon: faRulerVertical,
      color: '#52C41A',
      fields: [
        {
          name: 'height',
          label: 'Height',
          placeholder: '170',
          min: 50,
          max: 250,
          suffix: 'cm'
        }
      ]
    },
    {
      key: 'weight',
      title: 'Weight',
      icon: faWeightScale,
      color: '#FA8C16',
      fields: [
        {
          name: 'weight',
          label: 'Weight',
          placeholder: '70',
          min: 20,
          max: 300,
          step: 0.1,
          suffix: 'kg'
        }
      ]
    },
    {
      key: 'waist',
      title: 'Waist Circumference',
      icon: faRuler,
      color: '#722ED1',
      fields: [
        {
          name: 'waist',
          label: 'Waist',
          placeholder: '80',
          min: 40,
          max: 200,
          suffix: 'cm'
        }
      ]
    }
  ]

  // Watch for height and weight changes to calculate BMI
  useEffect(() => {
    const formValues = form.getFieldsValue()
    if (formValues.height && formValues.weight && enabledStats.height && enabledStats.weight) {
      const bmi = calculateBMI(formValues.weight, formValues.height)
      setCalculatedBMI(bmi)
    } else {
      setCalculatedBMI(null)
    }
  }, [form, enabledStats])

  const handleFormChange = () => {
    const formValues = form.getFieldsValue()
    if (formValues.height && formValues.weight && enabledStats.height && enabledStats.weight) {
      const bmi = calculateBMI(formValues.weight, formValues.height)
      setCalculatedBMI(bmi)
    } else {
      setCalculatedBMI(null)
    }
  }

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const timestamp = dayjs()
      const dataPromises = []

      // Process each enabled stat
      const stats = ['height', 'weight', 'waist']
      stats.forEach((stat) => {
        if (enabledStats[stat] && values[stat]) {
          dataPromises.push(
            createClinicDataRecords({
              dateCreated: timestamp,
              createdBy: agiliteUser?._id,
              userRef: bookingData?.userRef || userRef,
              bookingRef: bookingData?._id || '',
              origin: 'booking',
              category: 'general',
              key: stat,
              value: values[stat]
            })
          )
        }
      })

      // Add BMI if both height and weight are present
      if (calculatedBMI && enabledStats.height && enabledStats.weight) {
        dataPromises.push(
          createClinicDataRecords({
            dateCreated: timestamp,
            createdBy: agiliteUser?._id,
            userRef: bookingData?.userRef || userRef,
            bookingRef: bookingData?._id || '',
            origin: 'booking',
            category: 'general',
            key: 'bmi',
            value: Number(parseFloat(calculatedBMI).toFixed(2))
          })
        )
      }

      if (dataPromises.length === 0) {
        message.warning('Please enter at least one measurement')
        setLoading(false)
        return
      }

      const results = await Promise.all(dataPromises)

      // Send via WebSocket for real-time updates
      if (bookingData?._id && websocketConnection?.isConnected) {
        websocketConnection.socket.send(
          JSON.stringify({
            bookingRef: bookingData._id,
            data: results
          })
        )
      }

      message.success('General stats captured successfully')

      // Show notification with details
      notification.success({
        message: 'General Stats Saved',
        description: (
          <div>
            <p style={{ margin: '8px 0' }}>Successfully captured:</p>
            <ul style={{ marginLeft: '20px', marginBottom: 0 }}>
              {enabledStats.height && values.height && <li>Height: {values.height} cm</li>}
              {enabledStats.weight && values.weight && <li>Weight: {values.weight} kg</li>}
              {enabledStats.waist && values.waist && <li>Waist: {values.waist} cm</li>}
              {calculatedBMI && <li>BMI: {calculatedBMI} (auto-calculated)</li>}
            </ul>
          </div>
        ),
        icon: <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#52c41a' }} />,
        placement: 'topRight',
        duration: 4
      })

      form.resetFields()
      setCalculatedBMI(null)
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error capturing general stats:', error)

      // Show specific error message
      if (error.errorFields) {
        message.error('Please fill in all required fields')
      } else {
        message.error(handleError(error) || 'Failed to capture general stats')
      }
    } finally {
      setLoading(false)
    }
  }, [form, enabledStats, agiliteUser, bookingData, userRef, websocketConnection, onSuccess, onClose, calculatedBMI])

  const handleToggleStat = (statKey) => {
    setEnabledStats((prev) => ({
      ...prev,
      [statKey]: !prev[statKey]
    }))
  }

  const getReferenceText = (key) => {
    const range = GENERAL_STAT_REFERENCE_RANGES[key]
    if (!range) return ''

    if (key === 'waist') {
      return 'Normal: <94cm (men), <80cm (women)'
    }
    return `Normal range: ${range.min} - ${range.max} ${range.unit}`
  }

  return (
    <Modal
      className='general-stats-capture-modal'
      title={
        <Space>
          <FontAwesomeIcon icon={faChartLine} style={{ color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0 }}>
            Capture General Stats
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={handleSubmit}
          icon={<FontAwesomeIcon icon={faCalculator} />}
        >
          Save General Stats
        </Button>
      ]}
      centered
    >
      <Form form={form} layout='vertical' onValuesChange={handleFormChange}>
        <Row gutter={[16, 16]}>
          {statConfigs.map((config) => (
            <Col span={12} key={config.key}>
              <Card
                size='small'
                styles={{
                  body: {
                    background: enabledStats[config.key] ? hexToRGBA(config.color, 0.05) : '#f5f5f5',
                    transition: 'all 0.3s ease'
                  },
                  header: {
                    background: enabledStats[config.key] ? hexToRGBA(config.color, 0.1) : '#fafafa',
                    borderBottom: `2px solid ${enabledStats[config.key] ? config.color : '#d9d9d9'}`,
                    padding: '8px 12px'
                  }
                }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space size='small'>
                      <FontAwesomeIcon
                        icon={config.icon}
                        style={{
                          color: enabledStats[config.key] ? config.color : '#999',
                          fontSize: '16px'
                        }}
                      />
                      <Text
                        strong
                        style={{
                          color: enabledStats[config.key] ? '#000' : '#999'
                        }}
                      >
                        {config.title}
                      </Text>
                    </Space>
                    <Tooltip title={enabledStats[config.key] ? 'Disable' : 'Enable'}>
                      <Switch
                        size='small'
                        checked={enabledStats[config.key]}
                        onChange={() => handleToggleStat(config.key)}
                      />
                    </Tooltip>
                  </div>
                }
              >
                {enabledStats[config.key] ? (
                  <>
                    {config.fields.map((field) => (
                      <Form.Item
                        key={field.name}
                        name={field.name}
                        label={field.label}
                        rules={[
                          {
                            required: true,
                            message: `Please enter ${field.label.toLowerCase()}`
                          }
                        ]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          placeholder={field.placeholder}
                          min={field.min}
                          max={field.max}
                          step={field.step || 1}
                          addonAfter={field.suffix}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    ))}
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      {getReferenceText(config.key)}
                    </Text>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text type='secondary'>Click the toggle to enable</Text>
                  </div>
                )}
              </Card>
            </Col>
          ))}

          {/* BMI Display Card */}
          {calculatedBMI && (
            <Col span={12}>
              <Card
                size='small'
                styles={{
                  body: {
                    background: hexToRGBA('#1890FF', 0.05)
                  },
                  header: {
                    background: hexToRGBA('#1890FF', 0.1),
                    borderBottom: '2px solid #1890FF',
                    padding: '8px 12px'
                  }
                }}
                title={
                  <Space size='small'>
                    <FontAwesomeIcon
                      icon={faChartLine}
                      style={{
                        color: '#1890FF',
                        fontSize: '16px'
                      }}
                    />
                    <Text strong>BMI (Auto-calculated)</Text>
                  </Space>
                }
              >
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <Title level={3} style={{ margin: 0, color: '#1890FF' }}>
                    {calculatedBMI}
                  </Title>
                  <Text type='secondary' style={{ fontSize: '12px' }}>
                    {getReferenceText('bmi')}
                  </Text>
                </div>
              </Card>
            </Col>
          )}
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ textAlign: 'center' }}>
          <Text type='secondary'>
            Enable or disable individual measurements as needed. BMI will be automatically calculated when both height
            and weight are entered.
          </Text>
        </div>
      </Form>
    </Modal>
  )
}

export default React.memo(GeneralStatsCaptureModal)
